import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleTodayForecast,
  type HuddleTodayForecastResult,
  getHuddleTodayForecastOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleTodayForecastAppointmentsToTableRows,
  mapHuddleTodayForecastSummaryToMetrics,
  type HuddleTodayForecastTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleTodayForecastOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleTodayForecastResult = {
  data: HuddleTodayForecastResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  appointmentRows: HuddleTodayForecastTableRow[]
}

function useHuddleTodayForecastFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleTodayForecastOptions & {
  fetcher: HuddleDateFetcher<HuddleTodayForecastResult>
}): UseHuddleTodayForecastResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: "Failed to load today's forecast",
  })

  const summaryMetrics = useMemo(
    () => mapHuddleTodayForecastSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const appointmentRows = useMemo(
    () => mapHuddleTodayForecastAppointmentsToTableRows(data?.appointments),
    [data?.appointments],
  )

  return { data, loading, error, summaryMetrics, appointmentRows }
}

export function useHuddleTodayForecast(
  options: UseHuddleTodayForecastOptions,
): UseHuddleTodayForecastResult {
  return useHuddleTodayForecastFromFetcher({
    ...options,
    fetcher: getHuddleTodayForecast,
  })
}
export function useHuddleTodayForecastOverview(
  options: UseHuddleTodayForecastOptions,
): UseHuddleTodayForecastResult {
  return useHuddleTodayForecastFromFetcher({
    ...options,
    fetcher: getHuddleTodayForecastOverview,
  })
}
